import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Download, FileText, FileJson, Loader2 } from 'lucide-react';
import { SharePointEmbeddedService } from '../../services/sharePointEmbeddedService';
import { SPEAuthProvider } from '../../services/speAuthProvider';
import { documentsLogger } from '../../utils/logger';

interface TextFileViewerProps {
  documentId: string;
  documentName: string;
  containerId: string;
  onDownload?: () => void;
}

/**
 * Custom text file viewer for MD, JSON, and other text-based files
 * Fetches raw content and displays with syntax highlighting
 */
export const TextFileViewer: React.FC<TextFileViewerProps> = ({
  documentId,
  documentName,
  containerId,
  onDownload
}) => {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [language, setLanguage] = useState<string>('text');

  useEffect(() => {
    // Determine language based on file extension
    const extension = documentName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'md':
      case 'markdown':
        setLanguage('markdown');
        break;
      case 'json':
        setLanguage('json');
        break;
      case 'js':
        setLanguage('javascript');
        break;
      case 'ts':
      case 'tsx':
        setLanguage('typescript');
        break;
      case 'css':
        setLanguage('css');
        break;
      case 'html':
        setLanguage('html');
        break;
      case 'xml':
        setLanguage('xml');
        break;
      case 'yaml':
      case 'yml':
        setLanguage('yaml');
        break;
      case 'txt':
      default:
        setLanguage('text');
        break;
    }

    fetchContent();
  }, [documentId, documentName]);

  const fetchContent = async () => {
    setIsLoading(true);
    setError(null);

    try {
      documentsLogger.debug('Fetching text file content', { documentId, documentName });
      
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);
      
      // Get the download URL for the file
      const downloadUrl = await speService.getDocumentDownloadUrl(containerId, documentId);
      
      // Fetch the content
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file content: ${response.statusText}`);
      }

      const text = await response.text();
      
      // For JSON files, try to pretty-print
      if (language === 'json') {
        try {
          const jsonObj = JSON.parse(text);
          setContent(JSON.stringify(jsonObj, null, 2));
        } catch {
          // If JSON parsing fails, use raw text
          setContent(text);
        }
      } else {
        setContent(text);
      }

      documentsLogger.info('Text file content loaded successfully', { 
        documentName, 
        contentLength: text.length 
      });
    } catch (err) {
      documentsLogger.error('Failed to load text file content', err);
      setError(err instanceof Error ? err.message : 'Failed to load file content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      documentsLogger.error('Failed to copy to clipboard', err);
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      // Create a blob and trigger download
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentName;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const getFileIcon = () => {
    const extension = documentName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'json':
        return <FileJson className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading file content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-red-400" />
          </div>
          <h4 className="text-lg font-medium text-red-300 mb-2">Failed to Load File</h4>
          <p className="text-red-200 mb-4">{error}</p>
          <button
            onClick={fetchContent}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Header with file info and actions */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          {getFileIcon()}
          <div>
            <h4 className="text-sm font-medium text-gray-200">{documentName}</h4>
            <p className="text-xs text-gray-400">
              {language === 'json' ? 'JSON' : language === 'markdown' ? 'Markdown' : language.toUpperCase()} • 
              {' '}{(content.length / 1024).toFixed(1)} KB
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCopyToClipboard}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </>
            )}
          </button>
          
          <button
            onClick={handleDownload}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
            title="Download file"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>

      {/* Content with syntax highlighting */}
      <div className="flex-1 overflow-auto">
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: '#0c0c0c',
            fontSize: '14px',
            lineHeight: '1.5',
            minHeight: '100%'
          }}
          showLineNumbers={true}
          wrapLines={true}
          wrapLongLines={true}
        >
          {content}
        </SyntaxHighlighter>
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400">
        <div className="flex items-center justify-between">
          <span>
            {content.split('\n').length} lines • {content.length} characters
          </span>
          <span>
            {language === 'json' && 'JSON formatted'} 
            {language === 'markdown' && 'Markdown preview available'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TextFileViewer;